import { useEffect, useMemo, useState, type ReactElement } from 'react';
import type {
  EmailCaptureSettingsPatch,
  EmailToBoardAddressView,
  List,
} from '@molt/shared';
import { Button, Menu, Notice, TextField, type MenuItem } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface BoardEmailToBoardSectionProps {
  readonly address: EmailToBoardAddressView | undefined;
  readonly lists: readonly List[];
  readonly loading?: boolean | undefined;
  readonly loadError?: string | undefined;
  readonly canConfigure: boolean;
  readonly disabledReason: string | null;
  readonly regenerateBusy?: boolean | undefined;
  readonly settingsBusy?: boolean | undefined;
  readonly regenerateError?: string | undefined;
  readonly settingsError?: string | undefined;
  readonly onRegenerate: () => void;
  readonly onSaveSettings: (body: EmailCaptureSettingsPatch) => void;
}

/* FE-80.1 — a module constant cannot call `t`, so this one holds the KEY and the
   render resolves it (the `WorkspaceSwitcher.tsx` / `BoardMapView.tsx:80-84`
   shape). Key ORDER is load-bearing: `Object.keys` below drives the order the two
   position menu items render in. `top` stays first. */
const POSITION_LABEL_KEYS = {
  top: 'integrations.emailToBoard.position.top',
  bottom: 'integrations.emailToBoard.position.bottom',
} as const;

function selectedListTitle(
  lists: readonly List[],
  listId: string | null,
  fallback: string,
): string {
  return lists.find((list) => list.id === listId)?.title ?? fallback;
}

function selectedCaptureListId(
  lists: readonly List[],
  captureListId: string | null,
): string {
  if (captureListId !== null && lists.some((list) => list.id === captureListId)) {
    return captureListId;
  }
  return lists[0]?.id ?? '';
}

export function BoardEmailToBoardSection({
  address,
  lists,
  loading = false,
  loadError,
  canConfigure,
  disabledReason,
  regenerateBusy,
  settingsBusy,
  regenerateError,
  settingsError,
  onRegenerate,
  onSaveSettings,
}: BoardEmailToBoardSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [listId, setListId] = useState<string>('');
  const [position, setPosition] =
    useState<EmailCaptureSettingsPatch['position']>('bottom');

  useEffect(() => {
    if (address === undefined) return;
    setListId(selectedCaptureListId(lists, address.captureListId));
    setPosition(address.capturePosition ?? 'bottom');
  }, [address, lists]);

  const listItems = useMemo<MenuItem[]>(
    () =>
      lists.map((list) => ({
        id: list.id,
        label: list.title,
        onSelect: () => undefined,
      })),
    [lists],
  );
  /* ⚠ `[t]`, not `[]`. These labels are chrome now, and a memo that closes over
     `t` without declaring it keeps announcing the PREVIOUS language after a
     switch — fe-plan residual 11, which no static-render assertion can see. `t`
     is memoised on the resolved catalogue, so the dependency costs nothing. */
  const positionItems = useMemo<MenuItem[]>(
    () =>
      (Object.keys(POSITION_LABEL_KEYS) as EmailCaptureSettingsPatch['position'][]).map(
        (value) => ({
          id: value,
          label: t(POSITION_LABEL_KEYS[value]),
          onSelect: () => undefined,
        }),
      ),
    [t],
  );
  const checking =
    loading || (canConfigure && address === undefined && loadError === undefined);
  const selectedListExists = lists.some((list) => list.id === listId);
  const configureDisabledReason = checking
    ? t('integrations.emailToBoard.reason.checking')
    : (loadError ??
      (!canConfigure
        ? (disabledReason ?? t('integrations.emailToBoard.reason.editorOnly'))
        : lists.length === 0
          ? t('integrations.emailToBoard.reason.needsList')
          : null));
  const saveDisabledReason =
    configureDisabledReason ??
    (listId === '' || !selectedListExists
      ? t('integrations.emailToBoard.reason.chooseDestination')
      : null);
  const regenerateBusyProps =
    regenerateBusy === undefined ? {} : { busy: regenerateBusy };
  const settingsBusyProps = settingsBusy === undefined ? {} : { busy: settingsBusy };
  const saveDisabledProps =
    saveDisabledReason === null
      ? {}
      : { disabled: true, disabledReason: saveDisabledReason };
  const destinationTitle = selectedListTitle(
    lists,
    listId,
    t('integrations.emailToBoard.chooseList'),
  );

  return (
    <section aria-labelledby="email-to-board-heading" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h3 id="email-to-board-heading" className="text-sm font-semibold text-ink">
          {t('integrations.emailToBoard.heading')}
        </h3>
        <p className="text-sm text-ink-muted">
          {t('integrations.emailToBoard.description')}
        </p>
      </div>

      {checking ? (
        <Notice tone="info" body={t('integrations.emailToBoard.checkingNotice')} />
      ) : loadError !== undefined ? (
        <Notice tone="bad" body={loadError} />
      ) : address === undefined ? (
        <Notice
          tone="locked"
          body={disabledReason ?? t('integrations.emailToBoard.lockedFallback')}
        />
      ) : (
        <TextField
          label={t('integrations.emailToBoard.addressLabel')}
          readOnly
          value={address.address}
        />
      )}

      <div className="flex flex-col gap-2">
        <Menu
          trigger={
            <Button
              type="button"
              variant="secondary"
              aria-label={t('integrations.emailToBoard.listTriggerLabel', {
                title: destinationTitle,
              })}
              disabled={configureDisabledReason !== null}
              disabledReason={configureDisabledReason ?? undefined}
            >
              {destinationTitle}
            </Button>
          }
          sections={[
            {
              id: 'email-destination-list',
              heading: t('integrations.emailToBoard.listMenuHeading'),
              selection: 'radio',
              value: listId,
              onValueChange: setListId,
              items: listItems,
            },
          ]}
        />
        <Menu
          trigger={
            <Button
              type="button"
              variant="secondary"
              aria-label={t('integrations.emailToBoard.positionTriggerLabel', {
                position: t(POSITION_LABEL_KEYS[position]),
              })}
              disabled={configureDisabledReason !== null}
              disabledReason={configureDisabledReason ?? undefined}
            >
              {t(POSITION_LABEL_KEYS[position])}
            </Button>
          }
          sections={[
            {
              id: 'email-card-position',
              heading: t('integrations.emailToBoard.positionMenuHeading'),
              selection: 'radio',
              value: position,
              onValueChange: (value) => {
                setPosition(value as EmailCaptureSettingsPatch['position']);
              },
              items: positionItems,
            },
          ]}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="primary"
            onClick={() => onSaveSettings({ listId, position })}
            {...settingsBusyProps}
            {...saveDisabledProps}
          >
            {t('integrations.emailToBoard.saveAction')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onRegenerate}
            disabled={configureDisabledReason !== null}
            disabledReason={configureDisabledReason ?? undefined}
            {...regenerateBusyProps}
          >
            {t('integrations.emailToBoard.resetAction')}
          </Button>
        </div>
      </div>

      {settingsError !== undefined ? <Notice tone="bad" body={settingsError} /> : null}
      {regenerateError !== undefined ? (
        <Notice tone="bad" body={regenerateError} />
      ) : null}
    </section>
  );
}
